import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { X, Music2, ArrowUpRight, Phone, Shield } from "lucide-react";
import { TopographicBackground, PaperGrain, GoldOrb, StarField } from "./TopographicBackground";
import { ArtDivider } from "./ArtDivider";
import { useSiteContent, siteImageUrl } from "../lib/siteContent";

const PORTRAIT =
  "https://customer-assets-lqy194kg.emergentagent.net/job_tactile-book/artifacts/30vxcfho_820BA664-87DA-4E94-9C3E-CC5DBB92A8A4.JPG";

const EASE = [0.22, 1, 0.36, 1];

export const AboutAuthor = ({ open, onClose, book, onOpenEcosystem }) => {
  const site = useSiteContent();
  const paragraphs = (book?.author_bio || "").split("\n\n").filter(Boolean);
  const callUrl = book?.links?.braced_call;
  const portrait = siteImageUrl(site?.about?.portrait) || PORTRAIT;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[90] overflow-y-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          data-testid="about-author-overlay"
        >
          <div className="fixed inset-0 watercolor-wash" />
          <PaperGrain />
          <GoldOrb className="right-[-80px] top-[-100px]" size={480} testId="about-orb" />
          <StarField count={14} className="opacity-60" />
          <TopographicBackground
            className="pointer-events-none absolute inset-x-0 top-0 h-[420px] w-full"
            opacity={0.5}
          />

          <div className="relative z-10 mx-auto flex min-h-full max-w-5xl flex-col px-6 py-10 sm:px-10 sm:py-16">
            <div className="mb-12 flex items-start justify-between sm:mb-16">
              <div>
                <span className="kicker text-gold-foil">About the Author</span>
                <span className="kicker mt-2 block text-ink-muted">
                  {site?.about?.role || "Writer · Musician · Somatic Guide"}
                </span>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                data-testid="close-about-btn"
                className="debossed-btn flex h-10 w-10 flex-none items-center justify-center rounded-full text-ink-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
              >
                <X strokeWidth={1.4} className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-14">
              <div className="md:col-span-4">
                <motion.div
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9, ease: EASE }}
                  className="book-shadow relative aspect-[3/4] overflow-hidden rounded-[3px] bg-gradient-to-b from-ivory-100 to-ivory-300"
                >
                  <img
                    src={portrait}
                    alt={`Portrait of ${book?.author || "the author"}`}
                    className="h-full w-full object-cover"
                    style={{ objectPosition: "32% 22%" }}
                    draggable={false}
                  />
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/12 via-transparent to-transparent"
                  />
                </motion.div>
                <p className="kicker mt-4 text-ink-muted">Casey Muze, in his own rhythm</p>
              </div>

              <div className="md:col-span-8">
                <motion.h2
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9, delay: 0.05, ease: EASE }}
                  className="headline text-[46px] leading-[0.96] text-ink sm:text-[64px]"
                >
                  {book?.author || "Casey Muze"}
                </motion.h2>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.9, delay: 0.12 }}
                  className="deck mt-5 max-w-xl text-xl sm:text-[22px]"
                >
                  {site?.about?.deck || "He teaches the body like a landscape — uneven in places, familiar in others, always worth tending."}
                </motion.p>
                <div className="gold-rule my-8 max-w-[180px]" />

                <div className="prose-editorial measure">
                  {paragraphs.map((p, i) => (
                    <motion.p
                      key={i}
                      initial={{ opacity: 0, y: 14 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.16 + i * 0.08, ease: EASE }}
                      className={i === 0 ? "dropcap text-[20px]" : "text-[19px]"}
                    >
                      {p}
                    </motion.p>
                  ))}
                </div>

                <div className="mt-10 flex flex-wrap items-center gap-4">
                  <a
                    href="https://caseyscadence.info"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="cadence-external-link"
                    className="debossed inline-flex items-center gap-2.5 rounded-full px-5 py-2.5 text-sm text-ink transition hover:-translate-y-0.5"
                  >
                    <Music2 strokeWidth={1.4} className="h-4 w-4 text-gold-deep" />
                    <span className="font-display text-base">{book?.cadence_label || "Casey's Cadence"}</span>
                    <span className="text-ink-muted">— music &amp; reflections</span>
                    <ArrowUpRight strokeWidth={1.5} className="h-4 w-4 text-ink-muted" />
                  </a>
                </div>
              </div>
            </div>

            <ArtDivider className="mt-16" label="Continue the journey" />
            <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-4 border-t border-gold/20 pt-10">
              <button
                type="button"
                onClick={onClose}
                data-testid="return-to-reader-btn"
                className="debossed-btn rounded-full px-7 py-3 font-display text-lg text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
              >
                Return to the book
              </button>
              {callUrl && (
                <a
                  href={callUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-braced-call"
                  className="inline-flex items-center gap-2 text-base text-gold-deep underline-offset-4 transition hover:gap-3 hover:underline"
                >
                  <Phone strokeWidth={1.4} className="h-4 w-4" />
                  Book a free Braced Call
                </a>
              )}
              {onOpenEcosystem && (
                <button
                  type="button"
                  onClick={onOpenEcosystem}
                  data-testid="about-open-ecosystem-btn"
                  className="inline-flex items-center gap-1.5 text-base text-ink-soft underline-offset-4 transition hover:gap-2.5 hover:text-gold-deep hover:underline"
                >
                  Explore the Ecosystem
                  <ArrowUpRight strokeWidth={1.6} className="h-4 w-4" />
                </button>
              )}
              <Link
                to="/privacy"
                onClick={onClose}
                data-testid="about-privacy-link"
                className="inline-flex items-center gap-1.5 text-base text-ink-soft underline-offset-4 transition hover:gap-2.5 hover:text-gold-deep hover:underline"
              >
                <Shield strokeWidth={1.5} className="h-4 w-4" />
                Privacy policy
              </Link>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
